/** 项目状态与进度表达；完成量使用服务端真实数据，不模拟模型内部百分比。 */
import { useEffect, useState } from 'react';
import { type ProjectProgress, type ProjectTask, projectExecutionActive } from '~/data/queries';
import { fmtDateTime, fmtDurationSeconds } from '~/lib/format';
import { StatusChip, type StatusTone } from '~/ui/StatusChip';
import '../LandingTasksPanel.css';
import type { LandingProjectDto } from '@novel-admin/shared';
import { landingOneLineStyle } from './landing-collapsible-note';

export function projectHtmlReadinessReason(chapters: LandingProjectDto['chapters']): string | null {
  if (chapters.ready >= chapters.selected) return null;
  const blocked = chapters.blocked ?? [];
  const details = blocked
    .slice(0, 3)
    .map((chapter) => `第 ${chapter.chapter_index} 章：${chapter.reason}`)
    .join('；');
  return `HTML 就绪 ${chapters.ready}/${chapters.selected}；${details || '所选章节缺少当前有效的已采用正文，请查看书籍章节'}${blocked.length > 3 ? `；另有 ${blocked.length - 3} 章未就绪` : ''}`;
}
const labels: Record<string, string> = {
  PENDING: '排队中',
  QUEUED: '排队中',
  RUNNING: '处理中',
  WAITING: '等待依赖',
  WAITING_DEPENDENCY: '等待依赖',
  RETRYING: '重试中',
  SUCCEEDED: '成功',
  COMPLETED: '完成',
  PARTIAL: '部分完成',
  PARTIALLY_SUCCEEDED: '部分完成',
  FAILED: '执行失败',
  CANCELLED: '已取消',
  CANCELED: '已取消',
  NO_CHANGE: '无需修改',
  REUSED: '已复用',
  NOT_PROCESSED: '未处理',
  NOT_REVIEWED: '未审核',
  UNCHANGED: '无需修改',
  ISSUES: '发现问题',
  NOT_STARTED: '未处理',
  NONE: '未处理',
  PASSED: '审核通过',
  ISSUES_FOUND: '发现问题',
  NEEDS_REVIEW: '待复核',
  GENERATED: '已生成',
  GENERATING: '生成中',
  GENERATE_FAILED: '生成失败',
  PUBLISH_FAILED: '发布失败',
  PUBLISHING: '发布中',
  PUBLISHED: '已发布',
  STALE: '待重新生成',
  DRAFT: '草稿',
  DUPLICATE: '重复待确认',
  ACTIVE: '使用中',
  ARCHIVED: '已归档',
};
export const projectOperationLabel: Record<string, string> = {
  review: 'AI 审核',
  desensitize: 'AI 脱敏',
  generate: 'HTML 生成',
  publish: '发布',
};
export function ProjectState({ status, label }: { status: string; label?: string }) {
  const tone: StatusTone = ['FAILED', 'GENERATE_FAILED', 'PUBLISH_FAILED'].includes(status)
    ? 'neg'
    : ['SUCCEEDED', 'COMPLETED', 'PASSED', 'GENERATED', 'REUSED', 'NO_CHANGE'].includes(status)
      ? 'pos'
      : [
            'PARTIAL',
            'PARTIALLY_SUCCEEDED',
            'ISSUES_FOUND',
            'ISSUES',
            'NEEDS_REVIEW',
            'STALE',
            'DUPLICATE',
          ].includes(status)
        ? 'warn'
        : [
              'PENDING',
              'QUEUED',
              'RUNNING',
              'GENERATING',
              'PUBLISHING',
              'WAITING',
              'WAITING_DEPENDENCY',
              'RETRYING',
            ].includes(status)
          ? 'info'
          : 'default';
  return <StatusChip status={status} label={label ?? labels[status] ?? status} tone={tone} />;
}
export function projectPhaseLabel(phase: string) {
  const names: Record<string, string> = {
    queued: '等待调度',
    snapshot: '读取正文快照',
    source: '读取正文',
    rule: '规则字符遮挡',
    review: 'AI 审核',
    desensitize: 'AI 脱敏',
    post_review: '旧复核阶段（已停用）',
    validate: '校验输出',
    render: '渲染 HTML',
    inline: '内嵌图片资源',
    'technical-validation': '技术校验',
    persist: '保存产物',
    publish: '发布产物',
    completed: '处理完成',
  };
  const backendNames: Record<string, string> = {
    PENDING: '等待调度',
    QUEUED: '等待调度',
    PREPARING: '准备处理',
    PREPARING_ASSETS: '准备图片资源',
    VALIDATING_ARTIFACT: '校验 HTML 产物',
    BOUNDARY_REVIEW: '审核章节拼接边界',
    BOUNDARY_REUSED: '复用已有拼接边界审核',
    BOUNDARY_COMPLETED: '边界审核完成',
    BOUNDARY_SKIPPED: '已跳过旧独立交接检查',
    WAITING_CHAPTERS: '等待章节处理完成',
    READING_CONTENT: '读取章节正文',
    RULE_PROCESSING: '规则字符遮挡',
    REVIEWING: 'AI 审核',
    DESENSITIZING: 'AI 脱敏',
    RECHECKING: '提交营销正文版本',
    VALIDATING: '校验输出',
    VALIDATE: '校验输出',
    COMMITTING: '提交营销正文版本',
    GENERATOR_RUNNING: '生成 HTML',
    RENDERING: '渲染 HTML',
    INLINING: '内嵌图片资源',
    VERIFYING: '核验产物',
    UPLOADING: '上传发布产物',
    PUBLISHING: '发布 HTML',
    COMPLETED: '处理完成',
    CANCELLED: '请求已取消',
    FAILED: '处理失败',
  };
  return backendNames[phase] ?? names[phase] ?? phase;
}
/**
 * 服务端可重试失败把子项置回 PENDING 并保留 error：AI 内容执行要到该项成功收尾才清（推进、排队都不清），
 * 生成 / 发布任务在重新领取时清。所以执行仍在进行、且没有子项终态 FAILED 时，进行中子项带的 error
 * 只是「上次尝试失败」。按子项实时状态判，不比对 execution.error：批次行的 error 是刷新时的快照，
 * 可能已与子项不同。
 */
export function projectExecutionRetryReason(
  execution: Pick<ProjectProgress, 'status' | 'error' | 'items'>,
): string | null {
  if (!execution.error || !projectExecutionActive(execution)) return null;
  if (execution.items.some((item) => item.status === 'FAILED')) return null;
  return (
    execution.items.find((item) => ['PENDING', 'RUNNING'].includes(item.status) && item.error)
      ?.error ?? null
  );
}
/**
 * 红字展示的失败原因。批次行 error 只由刷新写入、是子项原因的快照：执行仍在进行时它可能是重试中子项的，
 * 也可能是已被重新领取清掉的「后台将重试」。所以进行中只认已 FAILED 子项的实时原因，执行结束后才用 execution.error。
 */
export function projectExecutionFailureReason(
  execution: Pick<ProjectProgress, 'status' | 'error' | 'items'>,
): string | null {
  if (!execution.error || projectExecutionRetryReason(execution)) return null;
  if (!projectExecutionActive(execution)) return execution.error;
  return execution.items.find((item) => item.status === 'FAILED' && item.error)?.error ?? null;
}
/** 重试中提示不区分等待 / 已重试：AI 内容执行推进与排队同样是 PENDING 且保留旧原因，前端分不出来。 */
export const PROJECT_RETRY_NOTE = '上次尝试失败，执行仍在自动进行';
export function ProjectProgressSummary({ execution }: { execution: ProjectProgress }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!execution.started_at || execution.finished_at) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [execution.started_at, execution.finished_at]);
  const elapsed = execution.started_at
    ? Math.max(
        0,
        Math.floor(
          ((execution.finished_at ? new Date(execution.finished_at).getTime() : now) -
            new Date(execution.started_at).getTime()) /
            1000,
        ),
      )
    : null;
  const retryReason = projectExecutionRetryReason(execution);
  const failureReason = projectExecutionFailureReason(execution);
  return (
    <div className="vstack" style={{ gap: 5 }} aria-live="polite">
      <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
        <ProjectState status={execution.status} />
        <span>
          {projectOperationLabel[execution.operation] ?? execution.operation} ·{' '}
          {projectPhaseLabel(execution.phase)}
        </span>
      </div>
      <span className="mono muted">
        完成 {execution.completed}/{execution.total}
        {execution.reused ? ` · 复用 ${execution.reused}` : ''}
        {execution.failed ? ` · 失败 ${execution.failed}` : ''}
      </span>
      {execution.total > 0 && (
        <progress
          className="landing-project-progress"
          aria-label="实际完成进度"
          value={Math.min(execution.completed, execution.total)}
          max={execution.total}
        />
      )}
      {retryReason ? (
        // 执行记录卡整张是按钮，这里不能再放「详情」按钮；一行截断，悬停看全文，完整原因在执行详情里。
        <span
          className="muted"
          title={retryReason}
          style={{ overflowWrap: 'anywhere', ...landingOneLineStyle }}
        >
          {PROJECT_RETRY_NOTE}：{retryReason}
        </span>
      ) : failureReason ? (
        <span role="alert" style={{ color: 'var(--neg)', overflowWrap: 'anywhere' }}>
          {failureReason}
        </span>
      ) : null}
      {execution.started_at && (
        <span className="mono muted">
          开始 {fmtDateTime(execution.started_at)} · {execution.finished_at ? '总耗时' : '已耗时'}{' '}
          {fmtDurationSeconds(elapsed ?? 0)}
          {/* 心跳只在运行中有意义：说明后台仍在推进，结束后不再展示。 */}
          {execution.heartbeat_at && !execution.finished_at
            ? ` · 最近活动 ${fmtDurationSeconds((now - new Date(execution.heartbeat_at).getTime()) / 1000)}前`
            : ''}
        </span>
      )}
    </div>
  );
}
export function projectDownloadAllowed(task: ProjectTask) {
  return (
    task.can_download &&
    !!task.generated_input_fingerprint &&
    task.generated_input_fingerprint === task.input_fingerprint &&
    !task.stale_reason
  );
}
export function toggleSelection(selection: Set<string>, id: string) {
  const next = new Set(selection);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}
